import React, { useEffect, useRef } from "react";
import axios from "axios";
import { Form, Field, ErrorMessage, Formik } from "formik";
import PropTypes from "prop-types";
import * as Yup from "yup";
import useCsrfToken from "../../../hooks/useCsrfToken";

// Yup validation schema
const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter a valid email address"),
});

// post to prospects endpoint
const postToProspects = async (values, csrfToken) => {
  const url = "/prospects";
  const headers = { "X-CSRF-Token": csrfToken };
  try {
    await axios.post(
      url,
      {
        ...values,
      },
      {
        headers,
      }
    );
  } catch (error) {
    throw error;
  }
};

// SignUpForm component
const SignUpForm = ({ closeModal, formAction, RECAPTCHA_SITE_KEY, source }) => {
  const csrfToken = useCsrfToken();
  const recaptchaRef = useRef(null);

  // add recaptcha script to DOM
  useEffect(() => {
    const scriptId = "recaptcha-script";
    const scriptSrc = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

    // check if the recaptcha script is already loaded
    const recaptchaScriptExists = () =>
      document.getElementById(scriptId) !== null;

    // add the recaptcha script if it's not already loaded
    if (!recaptchaScriptExists()) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    // remove recaptcha script on unmount
    return () => {
      const script = document.getElementById(scriptId);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    // make sure recaptcha is loaded
    if (
      typeof grecaptcha == "undefined" ||
      grecaptcha === undefined ||
      grecaptcha === null
    ) {
      alert(
        "There was an error submitting this form. Do you have an ad blocker installed?"
      );
      return;
    } else {
      try {
        // get recaptcha token
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action: formAction,
        });

        if (!token) {
          alert("There was an error submitting this form. Please try again.");
          return;
        }

        // get analytics user anonymous id
        let segment_anon_id = null;
        if (analytics && analytics?.user) {
          segment_anon_id = analytics.user().anonymousId();

          const a = new TgAnalytics();
          analytics.track("Email Capture Modal Submitted", {
            ...a.pageData,
          });
        }

        const postData = {
          ...values,
          segment_anon_id,
          rt_q4eaE4wQbPJo1RwszAPR: token,
        };

        await postToProspects({ ...postData }, csrfToken);
      } catch (error) {
        console.error("Error Signing up:", error);
      } finally {
        setSubmitting(false);
        closeModal();
        alert('Thank you for signing up! We will be in touch soon.');
      }
    }
  };

  return (
    <>
      <Formik
        initialValues={{ email: "", source, react: true }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting }) => {
          return (
            <Form className="center-items tg-vertical-space">
              <div className="flex flex-col md:flex-row ">
                <div className="md:mr-3">
                  <Field
                    aria-describedby="email-error"
                    type="text"
                    name="email"
                    className="form-control mb-2 md:mb-0"
                    placeholder="Email Address"
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    id="email-error"
                    name="email"
                    component="div"
                    className="error text-xs text-tgred pl-1 pt-3"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    value="Sign Up"
                    className="btn btn-solid max-w-button cursor-pointer"
                    data-disable-with="Sign Up"
                    disabled={isSubmitting}
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

SignUpForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  formAction: PropTypes.string.isRequired,
  RECAPTCHA_SITE_KEY: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
};

export default SignUpForm;



// WEBPACK FOOTER //
// ./app/javascript/bundles/ui/Forms/SignUp/index.jsx